import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Meta, Chip, Button, Card, PageTitle, Icons } from '../components/Atoms';
import Layout, { TopBar } from '../components/Layout';
import { LoadingState, EmptyState } from '../components/states';
import { useApi } from '../lib/api';
import axios from '../../config/axios';
import CommunityTabs from '../components/CommunityTabs';


function FriendRow({ friend, onRemove }) {
  return (
    <Card style={{
      display: 'flex', alignItems: 'center', gap: 14,
      marginBottom: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link to={`/g/${friend.username}`} style={{ textDecoration: 'none' }}>
          <span style={{ fontSize: 14, color: 'var(--t-text)' }}>
            {friend.displayName}
          </span>
        </Link>
        {friend.city && (
          <Meta style={{ marginLeft: 8 }}>
            <Icons.Plot size={10} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />{friend.city}
          </Meta>
        )}
      </div>
      <button type="button" onClick={onRemove} style={{
        padding: '4px 10px', borderRadius: 6,
        background: 'transparent', border: '1px solid var(--t-line-2)',
        color: 'var(--t-text-dim)', fontSize: 11, cursor: 'pointer',
        fontFamily: 'var(--t-mono)',
      }}>Usuń</button>
    </Card>
  );
}

export default function Friends() {
  const [tab, setTab] = useState('friends');
  const { data: friendsData, loading: fl, error: fe, refetch: refetchFriends } = useApi('/api/social/friends');
  const { data: pendingData, loading: pl, refetch: refetchPending } = useApi('/api/social/friends/pending');

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [msg, setMsg] = useState('');

  const doSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    setMsg('');
    try {
      const res = await axios.get(`/api/social/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (e) {
      setMsg('Nie udało się wyszukać. Sprawdź połączenie i spróbuj ponownie.');
    } finally {
      setSearching(false);
    }
  };

  const sendRequest = async (userId) => {
    try {
      await axios.post(`/api/social/friends/request/${userId}`);
      setMsg('✓ Zaproszenie wysłane');
      refetchPending();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Nie udało się wysłać zaproszenia. Spróbuj ponownie.');
    }
  };

  const acceptRequest = async (userId) => {
    await axios.post(`/api/social/friends/accept/${userId}`);
    refetchPending();
    refetchFriends();
  };

  const rejectRequest = async (userId) => {
    await axios.post(`/api/social/friends/reject/${userId}`);
    refetchPending();
  };

  const removeFriend = async (userId) => {
    await axios.delete(`/api/social/friends/${userId}`);
    refetchFriends();
  };

  const friends = Array.isArray(friendsData) ? friendsData : [];
  const pending = pendingData || { incoming: [], outgoing: [] };

  return (
    <Layout topBar={<TopBar left={<Meta>Społeczność</Meta>} />}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 32px 60px' }}>
        <PageTitle
          eyebrow="Społeczność"
          title="Znajomi"
          sub="Zarządzaj znajomymi i ich ogrodami."
        />

        <CommunityTabs active="friends" />

        {msg && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 16,
            background: msg.startsWith('✓') ? 'var(--t-sage-bg)' : 'var(--t-rust-bg)',
            borderLeft: `3px solid ${msg.startsWith('✓') ? 'var(--t-sage)' : 'var(--t-rust)'}`,
            fontSize: 13,
          }}>{msg}</div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[{ k: 'friends', l: `Znajomi (${friends.length})` }, { k: 'pending', l: `Zaproszenia (${pending.incoming.length + pending.outgoing.length})` }, { k: 'search', l: 'Szukaj' }].map(t => (
            <Chip key={t.k} selected={tab === t.k} onClick={() => setTab(t.k)}>{t.l}</Chip>
          ))}
        </div>

        {tab === 'friends' && (
          <div className="t-stagger">
            {fl ? <LoadingState /> :
            friends.length === 0 ? <EmptyState title="Brak znajomych" description="Dodaj pierwszą osobę przez wyszukiwarkę." /> :
            friends.map(f => <FriendRow key={f.id} friend={f} onRemove={() => removeFriend(f.id)} />)}
          </div>
        )}

        {tab === 'pending' && (
          <div className="t-stagger">
            {pl ? <LoadingState /> :
            <>
              {pending.incoming.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <Meta style={{ marginBottom: 10 }}>Przychodzące ({pending.incoming.length})</Meta>
                  {pending.incoming.map(p => (
                    <Card key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      marginBottom: 8,
                    }}>
                      <span style={{ flex: 1, fontSize: 14 }}>{p.displayName}</span>
                      <Button variant="primary" onClick={() => acceptRequest(p.id)} style={{ padding: '6px 12px', fontSize: 12, height: 'auto' }} bloomOnClick>Akceptuj</Button>
                      <Button variant="ghost" onClick={() => rejectRequest(p.id)} style={{ padding: '6px 12px', fontSize: 12, height: 'auto' }}>Odrzuć</Button>
                    </Card>
                  ))}
                </div>
              )}
              {pending.outgoing.length > 0 && (
                <div>
                  <Meta style={{ marginBottom: 10 }}>Wysłane ({pending.outgoing.length})</Meta>
                  {pending.outgoing.map(p => (
                    <Card key={p.id} style={{
                      marginBottom: 6, fontSize: 13, color: 'var(--t-text-dim)',
                    }}>→ {p.displayName}</Card>
                  ))}
                </div>
              )}
              {pending.incoming.length === 0 && pending.outgoing.length === 0 && (
                <EmptyState title="Brak zaproszeń" description="Nikogo jeszcze nie zaprosiłeś." />
              )}
            </>}
          </div>
        )}

        {tab === 'search' && (
          <div className="t-stagger">
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input type="text" aria-label="Szukaj znajomych" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder="Szukaj po nazwie…" minLength={2}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10,
                  background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
                  color: 'var(--t-text)', fontFamily: 'var(--t-sans)', fontSize: 14,
                }} />
              <Button variant="primary" onClick={doSearch} disabled={searching || searchQuery.length < 2}>
                {searching ? '…' : 'Szukaj'}
              </Button>
            </div>
            {searchResults && searchResults.length === 0 && (
              <EmptyState title="Brak wyników" description="Nie znaleziono profili publicznych." />
            )}
            {Array.isArray(searchResults) && searchResults.map(u => (
              <Card key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                marginBottom: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/g/${u.username}`} style={{ textDecoration: 'none' }}>
                    <span style={{ fontSize: 14, color: 'var(--t-text)' }}>{u.displayName}</span>
                  </Link>
                  {u.city && (
                    <Meta style={{ marginLeft: 8 }}>
                      <Icons.Plot size={10} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />{u.city}
                    </Meta>
                  )}
                </div>
                <Button variant="primary" onClick={() => sendRequest(u.id)} style={{ padding: '6px 12px', fontSize: 12, height: 'auto' }} bloomOnClick>+ Zaproś</Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
